import React, { useState, useEffect } from "react";
import { TEXT } from "Clients/ACCB/ACCBTheme";
import {
  Box,
  Breadcrumbs,
  Button,
  CircularProgress,
  Collapse,
  Grid,
  IconButton,
  makeStyles,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  Chip,
  Link
} from "@material-ui/core";
import { KeyboardBackspace, NavigateNext, ExpandLess, ExpandMore, PlayForWork } from "@material-ui/icons";
import { getCaseDetails, getCaseFiles, getScrutinyChecklist, getCaseTimeline, reviewScrutinyDocument, getScrutinyDocumentPreview } from "../../service";
import ShowProgress from "../ShowProgress";
import Swal from "sweetalert2";
import { displayEmail, getAdditionalEmails, getPartyOrganisation } from "../../utils";
import { openTrustedUrl } from "../../urlSafety";



const useStyles = makeStyles((theme) => ({
  root: {
    padding: theme.spacing(3),
    backgroundColor: "#ffffff",
    borderRadius: 4,
    textAlign: "left"
  },
  headerContainer: {
    display: "flex",
    flexDirection: "column",
    gap: '8px',
    marginBottom: theme.spacing(3)
  },
  breadcrumbs: {
    // fontSize: "0.875rem",
    "& a": {
      textDecoration: "none",
      color: theme.palette.text.secondary,
      fontWeight: 500,
      cursor: "pointer",
      "&:hover": {
        color: theme.palette.primary.main
      }
    }
  },
  caseName: {
    fontWeight: 600,
    fontSize: "1.2rem",
    color: TEXT.primary,
    wordBreak: "break-all"
  },
  card: {
    padding: theme.spacing(3),
    borderRadius: 4,
    backgroundColor: "#ffffff",
    border: "1px solid #E5E5E5",
    boxShadow: "none",
    display: "flex",
    flexDirection: "column",
    // width: "100%",
    height: "100%"
  },
  cardHeader: {
    marginBottom: theme.spacing(2),
    borderBottom: "1px solid #E5E5E5",
    paddingBottom: theme.spacing(1.5),
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center"
  },
  cardTitle: {
    fontWeight: 600,
    fontSize: "1.1rem",
    color: TEXT.primary
  },
  boldText: {
    fontWeight: 600
  },
  claimantDetailsRow: {
    padding: "8px 0",
    borderBottom: "1px solid #E5E5E5",
    "&:last-child": {
      borderBottom: "none"
    }
  },
  tableCell: {
    borderRight: "none",
    borderLeft: "none",
    padding: "12px 10px",
    fontSize: "0.875rem",
    borderBottom: "1px solid #E5E5E5"
  },
  outerHeader: {
    backgroundColor: "#eeeeee"
  },
  outerHeaderText: {
    color: TEXT.primary,
    fontWeight: 600
  },
  placeholderText: {
    color: TEXT.muted,
    fontSize: "0.875rem"
  },
  errorText: {
    fontSize: "0.875rem",
    color: TEXT.error
  },
  buttonContainer: {
    display: "flex",
    justifyContent: "flex-end",
    gap: theme.spacing(2) + 'px',
    marginTop: theme.spacing(3) + 'px',
  }
}));

function ExpandableRow({ classes, title, content }) {
  const [open, setOpen] = useState(false);
  return (
    <Box className={classes.claimantDetailsRow}>
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        onClick={() => setOpen(!open)}
        style={{
          cursor: "pointer",
          padding: "8px 12px",
          borderRadius: 4,
          backgroundColor: open ? "#EBF2FF" : "transparent"
        }}
      >
        <Typography className={classes.boldText} style={{ fontSize: "0.95rem" }}>
          {title}
        </Typography>
        {open ? <ExpandLess /> : <ExpandMore />}
      </Box>
      <Collapse in={open} timeout="auto" unmountOnExit>
        <Box style={{ padding: "12px 16px" }}>
          {content}
        </Box>
      </Collapse>
    </Box>
  );
}

export default function ScrutinyCheck({ scrutinyId, disputeId, onBack, profile }) {
  const classes = useStyles();
  const [caseData, setCaseData] = useState(null);
  const [caseFiles, setCaseFiles] = useState([]);
  const [timeline, setTimeline] = useState([]);
  const [checklistItems, setChecklistItems] = useState([]);
  const [previewData, setPreviewData] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [documentStatus, setDocumentStatus] = useState("pending");

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!disputeId) return;

    setLoading(true);
    setPreviewLoading(true);
    setError("");

    Promise.all([
      getCaseDetails(disputeId),
      getCaseFiles(disputeId),
      scrutinyId ? getScrutinyChecklist(scrutinyId) : Promise.resolve({ data: { items: [] } }),
      getCaseTimeline(disputeId),
      scrutinyId ? getScrutinyDocumentPreview(scrutinyId) : Promise.resolve(null)
    ])
      .then(([detailsRes, filesRes, checklistRes, timelineRes, previewRes]) => {
        setCaseData(detailsRes.data?.dispute_data || null);
        setCaseFiles(filesRes.data?.results || []);
        setChecklistItems(checklistRes.data?.items || []);
        setTimeline(timelineRes.data || []);
        setPreviewData(previewRes ? previewRes.data : null);
        setDocumentStatus(previewRes?.data?.status || "pending");
        setLoading(false);
        setPreviewLoading(false);
      })
      .catch((err) => {
        console.error("Error loading case check data:", err);
        setError("Failed to load Case details and checklist.");
        setLoading(false);
        setPreviewLoading(false);
      });
  }, [disputeId, scrutinyId]);

  const handleStatusChange = (itemId, value) => {
    setChecklistItems((prev) =>
      prev.map((item) => (item.item_id === itemId ? { ...item, status: value } : item))
    );
  };

  const handleCommentChange = (itemId, value) => {
    setChecklistItems((prev) =>
      prev.map((item) => (item.item_id === itemId ? { ...item, comment: value } : item))
    );
  };

  // Build the checklist answers payload from current UI state.
  const buildResponses = () =>
    checklistItems.map((item) => ({
      item_id: item.item_id,
      status: item.status || "yes",
      comment: item.comment || "",
    }));

  const handleApprove = () => {
    if (!scrutinyId) {
      Swal.fire("Error", "Scrutiny record ID not found.", "error");
      return;
    }

    Swal.fire({
      title: "Confirm Approval",
      text: "Are you sure you want to approve this document?",
      icon: "question",
      showCancelButton: true,
      confirmButtonText: "Approve",
      confirmButtonColor: "#2e7d32",
    }).then((result) => {
      if (result.isConfirmed) {
        // One call: save checklist answers + set status to approved.
        reviewScrutinyDocument(scrutinyId, {
          status: "approved",
          responses: buildResponses(),
        })
          .then((res) => {
            if (res.data?.success) {
              setDocumentStatus(res.data?.status);
              Swal.fire("Approved!", "Document approved successfully.", "success").then(() => {
                onBack();
              });
            } else {
              Swal.fire("Error", res.data?.message || "Failed to approve document.", "error");
            }
          })
          .catch((err) => {
            console.error("Error approving document:", err);
            Swal.fire("Error", "Failed to approve document.", "error");
          });
      }
    });
  };

  const handleMarkDefective = () => {
    if (!scrutinyId) {
      Swal.fire("Error", "Scrutiny record ID not found.", "error");
      return;
    }

    Swal.fire({
      title: "Mark as Defective",
      text: "Please specify the reason for marking this document as defective:",
      input: "text",
      inputPlaceholder: "Enter defect description...",
      showCancelButton: true,
      confirmButtonText: "Mark Defective",
      confirmButtonColor: "#d32f2f",
      inputValidator: (value) => {
        if (!value || !value.trim()) {
          return "A defect reason is required!";
        }
      }
    }).then((result) => {
      if (result.isConfirmed && result.value) {
        reviewScrutinyDocument(scrutinyId, {
          status: "defective",
          reason: result.value,
          responses: buildResponses(),
        })
          .then((res) => {
            if (res.data?.success) {
              setDocumentStatus(res.data?.status);
              Swal.fire("Marked Defective!", "Document marked as defective and memo generated.", "success").then(() => {
                onBack();
              });
            } else {
              Swal.fire("Error", res.data?.message || "Failed to mark document as defective.", "error");
            }
          })
          .catch((err) => {
            console.error("Error marking document defective:", err);
            Swal.fire("Error", "Failed to mark document as defective.", "error");
          });
      }
    });
  };


  if (loading) {
    return (
      <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" p={8} style={{ gap: "1rem" }}>
        <CircularProgress size={40} />
        <Typography className={classes.placeholderText}>
          Loading details & checklist...
        </Typography>
      </Box>
    );
  }

  if (error || !caseData) {
    return (
      <Box p={4} textAlign="center">
        <Typography className={classes.errorText}>{error || "No data available."}</Typography>
        <Button startIcon={<KeyboardBackspace />} onClick={onBack} style={{ marginTop: 16 }}>
          Back to Scrutiny List
        </Button>
      </Box>
    );
  }

  const dispute = caseData.dispute?.[0] || {};

  // Extract lawyers and segment into Claimant Counsels vs Respondent Counsels
  const lawyers = (caseData.professionals || []).filter(
    (p) => p.profile?.profile_type === "lawyer" || p.profile?.profile_type === "lawyer_rv"
  );

  const claimantLawyerIds = new Set();
  const respondentLawyerIds = new Set();

  caseFiles.forEach((file) => {
    if (file.type === "claimant") {
      (file.lawyer_rv || []).forEach((id) => claimantLawyerIds.add(id));
    } else {
      (file.lawyer_rv || []).forEach((id) => respondentLawyerIds.add(id));
    }
  });

  const claimantCounsels = lawyers.filter((l) => claimantLawyerIds.has(l.profile?.id));
  const respondentCounsels = lawyers.filter((l) => respondentLawyerIds.has(l.profile?.id));

  // Enable/Disable logic for action buttons
  const hasDefects = checklistItems.some((item) => item.status === "no");

  // Accordion rendering helpers

  // Extra notification addresses for a party/counsel (DisputePartyEmail);
  // nothing is rendered when there are none.
  const renderAdditionalEmails = (item) => {
    const emails = getAdditionalEmails(item);
    if (emails.length === 0) return null;
    return (
      <Typography variant="body2" style={{ marginBottom: 4, fontSize: "0.875rem", overflowWrap: "anywhere" }}>
        <span style={{ fontWeight: 600 }}>Additional E-mail Addresses:</span> {emails.join(", ")}
      </Typography>
    );
  };

  // Party type (and representative, for an organisation) of a claimant / respondent.
  const renderPartyOrganisation = (item) => {
    const { isOrganisation, representative } = getPartyOrganisation(item.profile);
    return (
      <>
        <Typography variant="body2" style={{ marginBottom: 4, fontSize: "0.875rem" }}>
          <span style={{ fontWeight: 600 }}>Party Type:</span> {isOrganisation ? "Organisation" : "Individual"}
        </Typography>
        {representative && (
          <Typography variant="body2" style={{ marginBottom: 4, fontSize: "0.875rem", overflowWrap: "anywhere" }}>
            <span style={{ fontWeight: 600 }}>Representative:</span> {representative}
          </Typography>
        )}
      </>
    );
  };

  const renderClaimantData = () => {
    const claimants = caseData.claimant || [];
    if (claimants.length === 0) return <Typography className={classes.placeholderText}>No claimant found.</Typography>;
    return claimants.map((item, idx) => {
      const name = [item.user?.first_name, item.user?.last_name].filter(Boolean).join(" ") || "—";
      const addr = [
        item.profile?.address,
        item.profile?.city,
        item.profile?.state,
        item.profile?.postal_code,
        item.profile?.nationality
      ].filter(Boolean).join(", ") || "—";
      return (
        <Box key={idx} mb={idx < claimants.length - 1 ? 2 : 0} pb={idx < claimants.length - 1 ? 1.5 : 0} style={{ borderBottom: idx < claimants.length - 1 ? "1px dashed #E5E5E5" : "none" }}>
          <Typography variant="body2" style={{ marginBottom: 4, fontSize: "0.875rem" }}><span style={{ fontWeight: 600 }}>Name:</span> {name}</Typography>
          {renderPartyOrganisation(item)}
          <Typography variant="body2" style={{ marginBottom: 4, fontSize: "0.875rem" }}><span style={{ fontWeight: 600 }}>E-mail Address:</span> {displayEmail(item.user?.email)}</Typography>
          {renderAdditionalEmails(item)}
          <Typography variant="body2" style={{ marginBottom: 4, fontSize: "0.875rem" }}><span style={{ fontWeight: 600 }}>Phone Number:</span> {item.profile?.phone_number || "—"}</Typography>
          <Typography variant="body2" style={{ fontSize: "0.875rem" }}><span style={{ fontWeight: 600 }}>Address:</span> {addr}</Typography>
        </Box>
      );
    });
  };

  const renderRespondentData = () => {
    const respondents = caseData.respondent || [];
    if (respondents.length === 0) return <Typography className={classes.placeholderText}>No respondent found.</Typography>;
    return respondents.map((item, idx) => {
      const name = [item.user?.first_name, item.user?.last_name].filter(Boolean).join(" ") || "—";
      const addr = [
        item.profile?.address,
        item.profile?.city,
        item.profile?.state,
        item.profile?.postal_code,
        item.profile?.nationality
      ].filter(Boolean).join(", ") || "—";
      return (
        <Box key={idx} mb={idx < respondents.length - 1 ? 2 : 0} pb={idx < respondents.length - 1 ? 1.5 : 0} style={{ borderBottom: idx < respondents.length - 1 ? "1px dashed #E5E5E5" : "none" }}>
          <Typography variant="body2" style={{ marginBottom: 4, fontSize: "0.875rem" }}><span style={{ fontWeight: 600 }}>Name:</span> {name}</Typography>
          {renderPartyOrganisation(item)}
          <Typography variant="body2" style={{ marginBottom: 4, fontSize: "0.875rem" }}><span style={{ fontWeight: 600 }}>E-mail Address:</span> {displayEmail(item.user?.email)}</Typography>
          {renderAdditionalEmails(item)}
          <Typography variant="body2" style={{ marginBottom: 4, fontSize: "0.875rem" }}><span style={{ fontWeight: 600 }}>Phone Number:</span> {item.profile?.phone_number || "—"}</Typography>
          <Typography variant="body2" style={{ fontSize: "0.875rem" }}><span style={{ fontWeight: 600 }}>Address:</span> {addr}</Typography>
        </Box>
      );
    });
  };

  const renderCounselData = (counsels) => {
    if (counsels.length === 0) return <Typography className={classes.placeholderText}>No counsel assigned.</Typography>;
    return counsels.map((item, idx) => {
      const name = [item.user?.first_name, item.user?.last_name].filter(Boolean).join(" ") || "—";
      return (
        <Box key={idx} mb={idx < counsels.length - 1 ? 2 : 0} pb={idx < counsels.length - 1 ? 1.5 : 0} style={{ borderBottom: idx < counsels.length - 1 ? "1px dashed #E5E5E5" : "none" }}>
          <Typography variant="body2" style={{ marginBottom: 4, fontSize: "0.875rem" }}><span style={{ fontWeight: 600 }}>Name:</span> {name}</Typography>
          <Typography variant="body2" style={{ marginBottom: 4, fontSize: "0.875rem" }}><span style={{ fontWeight: 600 }}>E-mail Address:</span> {displayEmail(item.user?.email)}</Typography>
          {renderAdditionalEmails(item)}
          <Typography variant="body2" style={{ marginBottom: 4, fontSize: "0.875rem" }}><span style={{ fontWeight: 600 }}>Phone Number:</span> {item.profile?.phone_number || "—"}</Typography>
          <Typography variant="body2" style={{ marginBottom: 4, fontSize: "0.875rem" }}><span style={{ fontWeight: 600 }}>Bar Enrollment:</span> {item.profile?.bar_enrollment || "—"}</Typography>
          <Typography variant="body2" style={{ fontSize: "0.875rem" }}><span style={{ fontWeight: 600 }}>Law Firm Name:</span> {item.profile?.law_firm_name || "—"}</Typography>
        </Box>
      );
    });
  };

  const renderCaseCategoryData = () => {
    const rawType = dispute.customer_type || "";
    const typeLower = rawType.toLowerCase();
    const typeCapitalized = rawType === "cmp" ? "CMP" : <span style={{ textTransform: 'capitalize' }}>{rawType}</span>;

    let ticketLabel = "";
    if (typeLower === "cmp") {
      ticketLabel = "CMP Number";
    } else if (typeLower === "reference") {
      ticketLabel = "MSEFC Number";
    }

    const showReferredFrom = typeLower === "reference";

    return (
      <Box>
        <Typography variant="body2" style={{ marginBottom: ticketLabel ? 4 : 0, fontSize: "0.875rem" }}>
          <span style={{ fontWeight: 600 }}>Case Category:</span> {typeCapitalized}
        </Typography>
        {ticketLabel && (
          <Typography variant="body2" style={{ marginBottom: showReferredFrom ? 4 : 0, fontSize: "0.875rem" }}>
            <span style={{ fontWeight: 600 }}>{ticketLabel}:</span> {dispute.request_ticket_number || "—"}
          </Typography>
        )}
        {showReferredFrom && (
          <Typography variant="body2" style={{ fontSize: "0.875rem" }}>
            <span style={{ fontWeight: 600 }}>Referred From:</span>{" "}
            {String(dispute.region || "").trim() || "—"}
          </Typography>
        )}
      </Box>
    );
  };

  const renderFileData = () => {
    if (previewLoading) {
      return <CircularProgress size={20} />;
    }
    if (!previewData) {
      return <Typography className={classes.placeholderText}>No file preview available.</Typography>;
    }
    return (
      <Box display="flex" alignItems="center" style={{ gap: "10px" }}>
        <Typography
          variant="body2"
          style={{
            fontSize: "0.875rem",
            minWidth: 0,
            wordBreak: "break-all",
          }}
        >
          {previewData.doc_name}
        </Typography>
        <IconButton
          style={{ flexShrink: 0 }}
          size="small"
          color="primary"
          onClick={() => {
            if (previewData.url) {
              openTrustedUrl(previewData.url, {
                context: "scrutiny document preview",
                onError: (message) => Swal.fire("Error", message, "error"),
              });
            }
          }}
          title="Download Document"
        >
          <PlayForWork />
        </IconButton>
      </Box>
    );
  };

  const getFilingStatusChip = (status) => {
    if (!status) return null;
    const s = status.toLowerCase();
    if (s === "approved") {
      return (
        <Chip
          label="Approved"
          style={{ backgroundColor: "#caf4d1", color: TEXT.success, fontWeight: 600, borderRadius: 4 }}
          size="small"
        />
      );
    }
    if (s === "defective") {
      return (
        <Chip
          label="Defective"
          style={{ backgroundColor: "#ffefeb", color: TEXT.error, fontWeight: 600, borderRadius: 4 }}
          size="small"
        />
      );
    }
    if (s === "not_checked" || s === "not checked") {
      return (
        <Chip
          label="Not Checked"
          style={{ backgroundColor: "#f2f4f7", color: TEXT.strong, fontWeight: 600, borderRadius: 4 }}
          size="small"
        />
      );
    }
    return (
      <Chip
        label={status}
        style={{ backgroundColor: "#f2f4f7", color: TEXT.strong, fontWeight: 600, borderRadius: 4 }}
        size="small"
      />
    );
  };

  return (
    <Box className={classes.root}>
      {/* 1. Page Header with Breadcrumbs */}
      <Box className={classes.headerContainer}>
        <Box display="flex" alignItems="center" style={{ gap: "10px" }}>
          <IconButton size="small" onClick={onBack}>
            <KeyboardBackspace />
          </IconButton>
          <Breadcrumbs separator={<NavigateNext fontSize="small" />} aria-label="breadcrumb" className={classes.breadcrumbs}>
            <Link onClick={onBack}>Scrutiny</Link>
            <Typography color="textPrimary">{dispute.tiac_custom_dispute_id || "Case Details"}</Typography>
          </Breadcrumbs>
        </Box>
        <Typography className={classes.caseName}>{dispute.name || "—"}</Typography>
      </Box>

      {/* 2. Side-by-Side Cards (Case Details & Stage-wise Trail) */}
      <Grid container spacing={3} style={{ marginBottom: 64 }}>
        <Grid item xs={12} md={6}>
          <Paper className={classes.card}>
            <Box className={classes.cardHeader}>
              <Typography className={classes.cardTitle}>
                {dispute.tiac_custom_dispute_id || "—"}
              </Typography>
              {getFilingStatusChip(documentStatus)}
            </Box>
            <Box>
              <ExpandableRow classes={classes} title="Claimant(s)" content={renderClaimantData()} />
              <ExpandableRow classes={classes} title="Respondent(s)" content={renderRespondentData()} />
              <ExpandableRow classes={classes} title="Claimant Counsel(s)" content={renderCounselData(claimantCounsels)} />
              <ExpandableRow classes={classes} title="Respondent Counsel(s)" content={renderCounselData(respondentCounsels)} />
              <ExpandableRow classes={classes} title="Case Category" content={renderCaseCategoryData()} />
              <ExpandableRow classes={classes} title="File" content={renderFileData()} />
            </Box>
          </Paper>
        </Grid>
        <Grid item xs={12} md={6}>
          <Paper className={classes.card}>
            <Box className={classes.cardHeader}>
              <Typography className={classes.cardTitle}>Stage-wise Trail</Typography>
            </Box>
            <Box style={{ overflowY: "auto", flex: 1, maxHeight: 400 }}>
              {timeline.length === 0 ? (
                <Typography className={classes.placeholderText}>No progress yet.</Typography>
              ) : (
                <ShowProgress
                  activeDispute={dispute}
                  timeLines={timeline}
                  profile={profile}
                />
              )}
            </Box>
          </Paper>
        </Grid>
      </Grid>

      {/* 3. Checklist Table */}
      <Box mb={2}>
        <Typography variant="h6" className={classes.boldText} style={{ marginBottom: 12 }}>
          Scrutiny Checklist
        </Typography>
      </Box>

      <TableContainer component={Paper} style={{ borderRadius: 8, boxShadow: "0 1px 4px rgba(0,0,0,0.08)" }}>
        <Table aria-label="checklist table" style={{ maxWidth: "100%", marginTop: 0 }}>
          <TableHead>
            <TableRow className={classes.outerHeader}>
              <TableCell className={`${classes.tableCell} ${classes.outerHeaderText}`} style={{ width: "8%" }}>Defect Code</TableCell>
              <TableCell className={`${classes.tableCell} ${classes.outerHeaderText}`} style={{ width: "50%" }}>Defect Description</TableCell>
              <TableCell className={`${classes.tableCell} ${classes.outerHeaderText}`} align="center" style={{ width: "15%" }}>Status</TableCell>
              <TableCell className={`${classes.tableCell} ${classes.outerHeaderText}`} style={{ width: "27%" }}>Comments</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {checklistItems.length === 0 ? (
              <TableRow>
                <TableCell colSpan={4} align="center" style={{ padding: 32 }}>
                  <Typography className={classes.placeholderText}>No checklist items found.</Typography>
                </TableCell>
              </TableRow>
            ) : (
              checklistItems.map((row, index) => {
                const serialNum = (index + 1 < 10 ? "0" : "") + (index + 1);
                return (
                  <TableRow key={row.item_id || index} hover>
                    <TableCell className={classes.tableCell}>
                      {serialNum}
                    </TableCell>
                    <TableCell className={classes.tableCell}>
                      <Typography style={{ fontSize: "0.875rem", fontWeight: 500 }}>
                        {row.description}
                      </Typography>
                      {/* {row.hint && (
                        <Typography
                          variant="caption"
                          style={{ fontStyle: "italic", display: "block", color: TEXT.muted, marginTop: 4 }}
                        >
                          {row.hint}
                        </Typography>
                      )} */}
                    </TableCell>
                    <TableCell className={classes.tableCell} align="center">
                      <TextField
                        variant="outlined"
                        size="small"
                        select
                        value={row.status || "yes"}
                        onChange={(e) => handleStatusChange(row.item_id, e.target.value)}
                        SelectProps={{ native: true }}
                        style={{ width: "90px" }}
                      >
                        <option value="yes">Yes</option>
                        <option value="no">No</option>
                        <option value="na">NA</option>
                      </TextField>
                    </TableCell>
                    <TableCell className={classes.tableCell}>
                      <TextField
                        variant="outlined"
                        size="small"
                        fullWidth
                        placeholder="Type comment (optional)"
                        value={row.comment || ""}
                        onChange={(e) => handleCommentChange(row.item_id, e.target.value)}
                      />
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* 4. Bottom Action Buttons */}
      <Box className={classes.buttonContainer}>
        <Button
          variant="contained"
          color="primary"
          // style={hasDefects ? { backgroundColor: "#d32f2f", color: TEXT.onDark } : {}}
          disabled={!hasDefects || documentStatus == "approved"}
          onClick={handleMarkDefective}
        >
          Mark as Defective
        </Button>
        <Button
          variant="contained"
          color="primary"
          disabled={hasDefects || documentStatus == "approved"}
          onClick={handleApprove}
        >
          Approve
        </Button>
      </Box>
    </Box>
  );
}
